import { useEffect, useMemo, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import FormError from "../../Form/FormError";
import FormDate from "../../Form/FormDate";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import FormRadioGroup from "../../Form/FormRadioGroup";
import Button from "../../Button";
import {
  addLocalNotification,
  useAuthState,
  useNotificationDispatch,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  requestCreateBusinessTransaction,
  requestUpdateBusinessTransaction,
  requestListBusinessPartner,
  resetNewBusinessTransaction,
  resetUpdateBusinessTransaction,
} from "../../../../Context";
import { cancelAPIRequest, resolvePickedDateTime } from "../../../../Utils";

const CASHBOOK_TYPES = [
  { value: "income", label: "cashbook.type.sale" },
  { value: "expense", label: "cashbook.type.expense" },
];

const CashbookTransactionModal = ({ open, onClose, transaction }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(transaction);

  const [type, setType] = useState(CASHBOOK_TYPES[0]);
  const [date, setDate] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [errors, setErrors] = useState({});

  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  const {
    request,
    newTransaction,
    newTransactionLoading,
    newTransactionError,
    updateTransaction,
    submitLoading,
    submitError,
    businessPartners,
    businessPartnerLoading,
    businessPartnerError,
  } = useBusinessTransactionState();

  const isLoading = newTransactionLoading || submitLoading;
  const formError = !isEdit ? newTransactionError : submitError;

  const partnerOptions = useMemo(
    () => businessPartners.map((p) => ({ value: p.id, label: p.name })),
    [businessPartners],
  );

  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      resetNewBusinessTransaction(dispatch);
      resetUpdateBusinessTransaction(dispatch);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && user?.mainBusiness?.id) {
      requestListBusinessPartner(dispatch, {
        page: 1,
        businessID: user.mainBusiness.id,
      });
    }
  }, [open, user?.mainBusiness?.id, dispatch]);

  // Populate form when editing
  useEffect(() => {
    if (transaction && open) {
      const isIncome = transaction.income > 0;
      setType(isIncome ? CASHBOOK_TYPES[0] : CASHBOOK_TYPES[1]);
      setDate(new Date(transaction.createdAt));
      setAmount(
        (isIncome ? transaction.income : transaction.expense).toString(),
      );
      setNote(transaction.note || "");
    } else if (!transaction && open) {
      resetFormFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, open]);

  // Set partner once partners load (for edit)
  useEffect(() => {
    if (transaction?.partnerID && businessPartners.length > 0) {
      const partner = businessPartners.find(
        (p) => p.id === transaction.partnerID,
      );
      if (partner) {
        setSelectedPartner({ value: partner.id, label: partner.name });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessPartners, transaction?.partnerID]);

  // Create success
  useEffect(() => {
    if (newTransaction?.id && !isEdit && open) {
      addLocalNotification(notificationDispatch, {
        id: `create-cashbook-${newTransaction.id}`,
        type: "success",
        title: "common.success",
        body: "cashbook.notifications.createSuccess",
        showFull: true,
      });
      resetNewBusinessTransaction(dispatch);
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newTransaction?.id]);

  // Update success
  useEffect(() => {
    if (updateTransaction?.id && isEdit && open) {
      addLocalNotification(notificationDispatch, {
        id: `edit-cashbook-${updateTransaction.id}`,
        type: "success",
        title: "common.success",
        body: "cashbook.notifications.updateSuccess",
        showFull: true,
      });

      resetUpdateBusinessTransaction(dispatch);
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTransaction?.id]);

  const resetFormFields = () => {
    setType(CASHBOOK_TYPES[0]);
    setDate(new Date());
    setAmount("");
    setNote("");
    setSelectedPartner(null);
    setErrors({});
  };

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  // The field starts and clears to null, so a first day click arrives as local
  // midnight. Left alone, every cash book entry made that day would serialise to
  // the same instant and the list could not be ordered. Once a date is set the
  // time field owns the clock and is never restamped.
  const handleDateChange = (value) => {
    setDate(resolvePickedDateTime(value, date));
    clearError("date");
  };

  const handleClose = () => {
    resetFormFields();
    resetNewBusinessTransaction(dispatch);
    resetUpdateBusinessTransaction(dispatch);
    onClose();
  };

  const validate = () => {
    const newErrors = {};
    if (!type) newErrors.type = "cashbook.modal.errors.typeRequired";
    if (!date) newErrors.date = "cashbook.modal.errors.dateRequired";
    if (
      !amount ||
      amount.trim() === "" ||
      Number(amount.replace(/\D/g, "")) <= 0
    ) {
      newErrors.amount = "cashbook.modal.errors.amountRequired";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const numericAmount = Number(amount.replace(/\D/g, ""));
    const isIncome = type.value === "income";

    const base = {
      type: "SALE",
      createdAt: date.toISOString(),
      income: isIncome ? numericAmount : 0,
      expense: isIncome ? 0 : numericAmount,
      note: note || "",
      ...(selectedPartner?.value && { partnerID: selectedPartner.value }),
    };

    if (isEdit) {
      requestUpdateBusinessTransaction(dispatch, {
        input: { ...base, id: transaction.id },
      });
    } else {
      requestCreateBusinessTransaction(dispatch, {
        input: { ...base, businessID: user.mainBusiness.id },
      });
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={handleClose}
      title={isEdit ? "cashbook.modal.title.edit" : "cashbook.modal.title.add"}
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={writeBlocked || isLoading}
            loading={isLoading}
            type="submit"
            form="cashbook-transaction-form"
            variant="primary"
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </div>
      }
    >
      <form
        id="cashbook-transaction-form"
        className="space-y-4 mb-6"
        onSubmit={handleSubmit}
      >
        <WriteBlockGate>
        <FormError error={formError} />

        <Fieldset
          disabled={isLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          <Field>
            <FormRadioGroup
              required
              label="cashbook.modal.fields.type"
              name="cashbook-type"
              options={CASHBOOK_TYPES}
              value={type}
              onChange={handleFieldChange(setType, "type")}
              error={errors.type}
            />
          </Field>

          <Field>
            <FormDate
              required
              label="cashbook.modal.fields.date"
              name="date"
              selected={date}
              onChange={handleDateChange}
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeInput
              timeInputLabel={t("common.time")}
              placeholderText={t("cashbook.modal.placeholders.date")}
              onClear={() => setDate(null)}
              error={errors.date}
            />
          </Field>

          <Field>
            <FormInput
              required
              label="cashbook.modal.fields.amount"
              id="cashbook-amount"
              name="amount"
              value={amount}
              onChange={handleFieldChange(setAmount, "amount")}
              onClear={() => setAmount("")}
              placeholder={t("cashbook.modal.placeholders.amount")}
              numberFormat={{ thousandSeparator: true }}
              prefix="Rp"
              error={errors.amount}
            />
          </Field>

          <Field>
            <FormInput
              hint="common.optional"
              label="cashbook.modal.fields.note"
              id="cashbook-note"
              name="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onClear={() => setNote("")}
              placeholder={t("cashbook.modal.placeholders.note")}
            />
          </Field>

          <Field>
            <FormSelect
              hint="common.optional"
              label="cashbook.modal.fields.partner"
              id="cashbook-partner"
              name="partner"
              searchable
              options={partnerOptions}
              isLoading={businessPartnerLoading}
              value={selectedPartner}
              onChange={setSelectedPartner}
              placeholder={t("cashbook.modal.placeholders.partner")}
              error={businessPartnerError}
              isClearable
              onClear={() => setSelectedPartner(null)}
            />
          </Field>
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default CashbookTransactionModal;
